import type { BffCatalogSubscription } from '@/core/models/bff-catalog';
import type {
  BffBillingPeriod,
  BffPlanInterval,
  BffPlanType,
  BffPurchasablePlan,
} from '@/core/models/bff-purchasable-plan';
import {
  APP_SUBSCRIPTION_PRODUCT_CODES,
  EXCLUDED_APP_SUBSCRIPTION_PRODUCT_CODES,
} from '@/core/constants/product-ids';
import { yearlyContractPrice } from '@/ui/utils/subscription/resolve-subscription-price-fields';

const GRID_PLAN_TYPES: BffPlanType[] = ['individual', 'smb', 'family'];

const BFF_PLAN_INTERVAL_VALUES = new Set<string>([
  'weekly',
  'monthly',
  'quarterly',
  'yearly',
  'permanent',
]);

const BFF_PLAN_TYPE_VALUES = new Set<string>([
  'individual',
  'family',
  'smb',
  'b2b',
  'partner',
  'complimentary',
]);

const LEGACY_PLAN_TYPE_MAP: Record<string, BffPlanType> = {
  personal: 'individual',
  individual: 'individual',
  family: 'family',
  smb: 'smb',
  b2b: 'b2b',
  partner: 'partner',
  complimentary: 'complimentary',
};

type CatalogPlanMeta = {
  type: BffPlanType;
  interval: BffPlanInterval;
  billingPeriod: BffBillingPeriod;
  seats?: { total: number };
};

const APP_SUBSCRIPTION_CODE_META: Record<string, CatalogPlanMeta> = {
  [APP_SUBSCRIPTION_PRODUCT_CODES.individual.monthly]: {
    type: 'individual',
    interval: 'monthly',
    billingPeriod: 'monthly',
  },
  [APP_SUBSCRIPTION_PRODUCT_CODES.individual.yearly]: {
    type: 'individual',
    interval: 'yearly',
    billingPeriod: 'yearly',
  },
  [APP_SUBSCRIPTION_PRODUCT_CODES.individual.lifetime]: {
    type: 'individual',
    interval: 'permanent',
    billingPeriod: 'yearly',
  },
  [APP_SUBSCRIPTION_PRODUCT_CODES.duo.monthly]: {
    type: 'smb',
    interval: 'monthly',
    billingPeriod: 'monthly',
    seats: { total: 2 },
  },
  [APP_SUBSCRIPTION_PRODUCT_CODES.duo.yearly]: {
    type: 'smb',
    interval: 'yearly',
    billingPeriod: 'yearly',
    seats: { total: 2 },
  },
  [APP_SUBSCRIPTION_PRODUCT_CODES.duo.lifetime]: {
    type: 'smb',
    interval: 'permanent',
    billingPeriod: 'yearly',
    seats: { total: 2 },
  },
  [APP_SUBSCRIPTION_PRODUCT_CODES.family.monthly]: {
    type: 'family',
    interval: 'monthly',
    billingPeriod: 'monthly',
    seats: { total: 4 },
  },
  [APP_SUBSCRIPTION_PRODUCT_CODES.family.yearly]: {
    type: 'family',
    interval: 'yearly',
    billingPeriod: 'yearly',
    seats: { total: 4 },
  },
  [APP_SUBSCRIPTION_PRODUCT_CODES.family.lifetime]: {
    type: 'family',
    interval: 'permanent',
    billingPeriod: 'yearly',
    seats: { total: 4 },
  },
};

export function normalizeBffPlanType(type: string): BffPlanType {
  const normalized = type.trim().toLowerCase();
  const mapped = LEGACY_PLAN_TYPE_MAP[normalized];

  if (mapped) {
    return mapped;
  }

  if (BFF_PLAN_TYPE_VALUES.has(normalized)) {
    return normalized as BffPlanType;
  }

  return normalized as BffPlanType;
}

export function normalizeBffPlanInterval(interval: string): BffPlanInterval {
  const normalized = interval.trim().toLowerCase();

  if (normalized === 'lifetime') {
    return 'permanent';
  }

  if (BFF_PLAN_INTERVAL_VALUES.has(normalized)) {
    return normalized as BffPlanInterval;
  }

  return normalized as BffPlanInterval;
}

function normalizeBffBillingPeriod(billingPeriod: string): BffBillingPeriod {
  const normalized = billingPeriod.trim().toLowerCase();

  if (normalized === 'monthly' || normalized === 'yearly') {
    return normalized;
  }

  return normalized as BffBillingPeriod;
}

function isV2PurchasablePlan(row: unknown): row is BffPurchasablePlan {
  if (typeof row !== 'object' || row === null) {
    return false;
  }

  const candidate = row as Record<string, unknown>;
  return (
    typeof candidate.type === 'string' &&
    typeof candidate.billingPeriod === 'string' &&
    typeof candidate.productCode === 'string'
  );
}

function isPurchasablePlan(plan: BffPurchasablePlan): boolean {
  if (!GRID_PLAN_TYPES.includes(plan.type)) {
    return false;
  }

  return (
    plan.interval === 'monthly' ||
    plan.interval === 'yearly' ||
    plan.interval === 'permanent'
  );
}

function normalizeV2PurchasablePlan(plan: BffPurchasablePlan): BffPurchasablePlan {
  const type = normalizeBffPlanType(plan.type);
  const interval = normalizeBffPlanInterval(plan.interval);
  const billingPeriod = normalizeBffBillingPeriod(plan.billingPeriod);
  const billingPrice = plan.billingPrice ?? plan.unitPrice;
  const contractPrice =
    plan.contractPrice ??
    plan.intervalPrice ??
    (interval === 'yearly' ? yearlyContractPrice(billingPrice) : billingPrice);
  const billingInterval = plan.billingInterval
    ? normalizeBffBillingPeriod(plan.billingInterval)
    : billingPeriod;
  const contractInterval = plan.contractInterval
    ? normalizeBffPlanInterval(plan.contractInterval)
    : interval;

  return {
    ...plan,
    type,
    interval,
    billingPeriod,
    billingPrice,
    unitPrice: billingPrice,
    contractPrice,
    intervalPrice: contractPrice,
    billingInterval,
    contractInterval,
  };
}

function catalogSubscriptionToPurchasablePlan(
  subscription: BffCatalogSubscription,
): BffPurchasablePlan | null {
  const productCode = subscription.code ?? subscription.id;

  if (EXCLUDED_APP_SUBSCRIPTION_PRODUCT_CODES.has(productCode)) {
    return null;
  }

  const meta = APP_SUBSCRIPTION_CODE_META[productCode];
  if (!meta) {
    return null;
  }

  const billingPrice = subscription.billingPrice ?? subscription.unitPrice;
  const contractPrice =
    subscription.contractPrice ??
    (meta.interval === 'yearly' ? yearlyContractPrice(billingPrice) : billingPrice);

  return {
    id: subscription.id,
    productCode,
    type: meta.type,
    interval: meta.interval,
    billingPeriod: meta.billingPeriod,
    billingPrice,
    unitPrice: billingPrice,
    contractPrice,
    intervalPrice: contractPrice,
    billingInterval: meta.billingPeriod,
    contractInterval: meta.interval,
    discountedUnitPrice: subscription.discountedUnitPrice,
    currency: subscription.currency,
    name: subscription.name,
    description: subscription.description,
    thumbnailURL: subscription.thumbnailUrl,
    displayText: subscription.displayText,
    displayAssets: subscription.displayAssets,
    seats: meta.seats,
  };
}

function normalizeCatalogSubscriptions(
  subscriptions: BffCatalogSubscription[],
): BffPurchasablePlan[] {
  return subscriptions
    .map(catalogSubscriptionToPurchasablePlan)
    .filter((plan): plan is BffPurchasablePlan => plan !== null);
}

/**
 * Normalizes catalog subscription wire data to purchasable grid plans.
 * Supports V2 plan DTOs and flat {@link BffCatalogSubscription} rows.
 */
export default function normalizePurchasablePlans(
  wire: BffCatalogSubscription[] | BffPurchasablePlan[] | undefined,
): BffPurchasablePlan[] {
  if (!wire?.length) {
    return [];
  }

  if (isV2PurchasablePlan(wire[0])) {
    return (wire as BffPurchasablePlan[])
      .map(normalizeV2PurchasablePlan)
      .filter(isPurchasablePlan);
  }

  return normalizeCatalogSubscriptions(wire);
}
